import { CheckCircle, XCircle, Loader } from 'lucide-react';

const BG_GRADIENT = {
  background: 'linear-gradient(135deg, #051C2C 0%, #003594 50%, #008AD8 100%)',
};

function CenteredCard({ borderColor, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center" style={BG_GRADIENT}>
      <div
        className="bg-white rounded-3xl p-10 max-w-md w-full shadow-2xl border-4"
        style={borderColor ? { borderColor } : {}}
      >
        {children}
      </div>
    </div>
  );
}

export function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={BG_GRADIENT}>
      <div className="text-white text-xl" data-testid="payment-loading">Cargando orden...</div>
    </div>
  );
}

export function NotFoundScreen() {
  return (
    <CenteredCard borderColor="#FED7AA">
      <div className="text-center" data-testid="payment-not-found">
        <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-12 h-12 text-orange-600" />
        </div>
        <h2 className="text-3xl font-bold mb-3" style={{ color: '#051C2C' }}>Orden No Encontrada</h2>
        <p className="text-gray-600 text-lg">Esta orden no existe o ha expirado.</p>
      </div>
    </CenteredCard>
  );
}

export function PaidScreen({ order }) {
  return (
    <CenteredCard borderColor="#86EFAC">
      <div className="text-center" data-testid="payment-already-paid">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-12 h-12 text-green-600" />
        </div>
        <h2 className="text-3xl font-bold mb-3" style={{ color: '#051C2C' }}>Orden Ya Pagada</h2>
        <p className="text-gray-600 mb-6 text-lg">Esta orden ya fue procesada exitosamente.</p>
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-200 mb-6">
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-600">Código de Orden:</p>
              <p className="text-2xl font-bold" style={{ color: '#003594' }}>{order.order_code}</p>
            </div>
            <div className="border-t border-green-200 pt-3">
              <p className="text-sm text-gray-600">Monto:</p>
              <p className="text-3xl font-bold text-green-600">
                {order.currency} {order.amount.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            ℹ️ Si necesitas ayuda, contacta al comercio que generó esta orden.
          </p>
        </div>
      </div>
    </CenteredCard>
  );
}

export function ExpiredOrCancelledScreen({ status }) {
  return (
    <CenteredCard borderColor="#D1D5DB">
      <div className="text-center" data-testid="payment-expired-or-cancelled">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-12 h-12 text-gray-600" />
        </div>
        <h2 className="text-3xl font-bold mb-3" style={{ color: '#051C2C' }}>
          Orden {status === 'expired' ? 'Expirada' : 'Cancelada'}
        </h2>
        <p className="text-gray-600 text-lg">Esta orden ya no está disponible para pago.</p>
      </div>
    </CenteredCard>
  );
}

export function SuccessScreen({ order }) {
  return (
    <CenteredCard borderColor="#008AD8">
      <div className="text-center" data-testid="payment-success">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-12 h-12 text-green-600" />
        </div>
        <h2 className="text-3xl font-bold mb-3" style={{ color: '#051C2C' }}>¡Pago Exitoso!</h2>
        <p className="text-gray-600 mb-6 text-lg">Tu pago ha sido procesado correctamente.</p>
        <div
          className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6 border-2"
          style={{ borderColor: '#008AD8' }}
        >
          <p className="text-sm text-gray-600 mb-2">Monto pagado:</p>
          <p className="text-4xl font-bold" style={{ color: '#003594' }}>
            {order.currency} {order.amount.toFixed(2)}
          </p>
        </div>
        {order.success_redirect_url && (
          <p className="text-sm text-gray-500 mt-6 flex items-center justify-center gap-2">
            <Loader className="w-4 h-4 animate-spin" />
            Redirigiendo en 3 segundos...
          </p>
        )}
      </div>
    </CenteredCard>
  );
}

export function FailedScreen({ error, onRetry }) {
  return (
    <CenteredCard borderColor="#FCA5A5">
      <div className="text-center" data-testid="payment-failed">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-12 h-12 text-red-600" />
        </div>
        <h2 className="text-3xl font-bold mb-3" style={{ color: '#051C2C' }}>Pago No Verificado</h2>
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-6">
          <p className="text-red-800 text-sm font-medium">
            {error || 'No pudimos verificar tu pago. Por favor verifica los datos e intenta nuevamente.'}
          </p>
        </div>
        <button
          onClick={onRetry}
          data-testid="payment-retry-btn"
          className="w-full py-4 px-6 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all"
          style={{ backgroundColor: '#003594' }}
        >
          Intentar Nuevamente
        </button>
      </div>
    </CenteredCard>
  );
}
